import React, { forwardRef, HTMLProps, memo, ReactElement, ReactNode, useCallback, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import classnames from 'classnames';

import './InputTextField.scss';

export type InputStatus = 'active' | 'error' | 'inactive' | 'disabled';

export interface InputTextFieldProps extends Omit<HTMLProps<HTMLInputElement | HTMLTextAreaElement>, 'label'> {
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  bottomClassName?: string;
  label?: ReactNode;
  status?: InputStatus;
  name: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'textarea';
  placeholder?: string;
  defaultValue?: string;
  bottomLabel?: string;
  forceHideLabel?: boolean;
  forceHideBottomLabel?: boolean;
  value?: string | number;
  disablePaste?: boolean;
  disableAutoComplete?: true;
  elementAfter?: ReactElement;
  currencyInput?: boolean;
  noShadow?: boolean;
}

export const InputTextField = forwardRef(
  (
    {
      className = '',
      labelClassName = '',
      inputClassName = '',
      bottomClassName = '',
      label,
      name,
      status = 'active',
      type = 'text',
      defaultValue,
      value,
      bottomLabel,
      forceHideLabel,
      forceHideBottomLabel,
      disablePaste,
      disableAutoComplete,
      elementAfter,
      onPaste,
      currencyInput,
      noShadow,
      // To fix `onChange` undefined while `value` defined error
      onChange = () => null,
      ...restProps
    }: InputTextFieldProps,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: any,
  ) => {
    const [isUserTyping, setIsUserTyping] = useState(false);

    const inputElementProps = {
      ...restProps,
      autoComplete: disableAutoComplete ? 'new-password' : 'on',
      className: `${
        status === 'disabled'
          ? 'InputTextField-input-disabled'
          : status === 'error'
          ? 'InputTextField-input-error'
          : 'InputTextField-input'
      } ${inputClassName}`,
      name,
      ref,
      value: value || defaultValue,
      onChange,
      readOnly: status === 'disabled',
      onPaste: useCallback(
        (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (disablePaste) e.preventDefault();
          if (onPaste) onPaste(e);
        },
        [disablePaste, onPaste],
      ),
      onKeyDown: useCallback(() => {
        setIsUserTyping(true);
      }, []),
      onBlur: useCallback(() => {
        setIsUserTyping(false);
      }, []),
      autoFocus: isUserTyping,
    };

    const InputElement = () => {
      if (type === 'textarea') {
        return <textarea aria-label={(label as string) || undefined} {...inputElementProps} />;
      }

      if (currencyInput) {
        return (
          <CurrencyInput
            aria-label={(label as string) || undefined}
            {...{ ...inputElementProps, step: undefined }}
            prefix="$"
            decimalsLimit={2}
          />
        );
      }

      // eslint-disable-next-line jsx-a11y/no-autofocus
      return <input aria-label={(label as string) || undefined} type={type} {...inputElementProps} />;
    };

    return (
      <div
        data-testid="InputTextField"
        key={`InputTextField-${name}`}
        className={classnames('InputTextField', className, {
          'mod-noShadow': noShadow,
        })}
      >
        {!forceHideLabel && <div className={`InputTextField-input-label  ${labelClassName}`}>{label}</div>}
        <InputElement key={`InputTextField-${name}`} />
        {!forceHideBottomLabel && (
          <div
            className={`${
              status === 'error' ? 'InputTextField-input-label-bottom-error' : 'InputTextField-input-label-bottom'
            } ${bottomClassName}`}
          >
            {bottomLabel}
          </div>
        )}
        {elementAfter}
      </div>
    );
  },
);

InputTextField.displayName = 'InputTextField';

export default memo(InputTextField);
